import type {
  AnteprimaImport,
  ConfermaAdmin,
  EsitoImport,
  Iscritto,
  Percorso,
  PercorsoInput,
  Ruolo,
  UtenteAdmin,
  VoceChecklist,
  Webinar,
  WebinarInput,
} from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { richiesta } from './client';

export interface Paginato<T> {
  elementi: T[];
  totale: number;
  pagina: number;
  perPagina: number;
}

export interface FiltriPaginati {
  q?: string;
  stato?: string;
  ruolo?: string;
  pagina?: number;
  perPagina?: number;
}

function query(filtri: FiltriPaginati): string {
  const p = new URLSearchParams();
  if (filtri.q) p.set('q', filtri.q);
  if (filtri.stato) p.set('stato', filtri.stato);
  if (filtri.ruolo) p.set('ruolo', filtri.ruolo);
  if (filtri.pagina) p.set('pagina', String(filtri.pagina));
  if (filtri.perPagina) p.set('perPagina', String(filtri.perPagina));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const chiaviAdmin = {
  percorsi: ['admin', 'percorsi'] as const,
  percorso: (id: string) => ['admin', 'percorso', id] as const,
  iscritti: (id: string, f: FiltriPaginati) => ['admin', 'iscritti', id, f] as const,
  webinarTutti: ['admin', 'webinar'] as const,
  webinar: (id: string) => ['admin', 'webinar', id] as const,
  conferme: (id: string, f: FiltriPaginati) => ['admin', 'conferme', id, f] as const,
  checklist: (id: string) => ['admin', 'checklist', id] as const,
  utenti: (f: FiltriPaginati) => ['admin', 'utenti', f] as const,
};

// ---- percorsi
export function usePercorsiAdmin() {
  return useQuery({ queryKey: chiaviAdmin.percorsi, queryFn: async () => (await richiesta<Percorso[]>('/admin/percorsi')).dati });
}
export function usePercorsoAdmin(id: string) {
  return useQuery({ queryKey: chiaviAdmin.percorso(id), queryFn: async () => (await richiesta<Percorso>(`/admin/percorsi/${id}`)).dati });
}
export function useCreaPercorso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PercorsoInput) => (await richiesta<Percorso>('/admin/percorsi', { method: 'POST', json: input })).dati,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaviAdmin.percorsi }),
  });
}
export function useAggiornaPercorso(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PercorsoInput) => (await richiesta<Percorso>(`/admin/percorsi/${id}`, { method: 'PUT', json: input })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorsi });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorso(id) });
    },
  });
}
export function useCambiaStatoPercorso(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (azione: 'pubblica' | 'chiudi') => (await richiesta<Percorso>(`/admin/percorsi/${id}/${azione}`, { method: 'POST' })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorsi });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorso(id) });
    },
  });
}

// ---- iscritti
export function useIscritti(percorsoId: string, filtri: FiltriPaginati) {
  return useQuery({
    queryKey: chiaviAdmin.iscritti(percorsoId, filtri),
    queryFn: async () => (await richiesta<Paginato<Iscritto>>(`/admin/percorsi/${percorsoId}/iscritti${query(filtri)}`)).dati,
    placeholderData: (prev) => prev,
  });
}
export function useAggiungiIscritto(percorsoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => (await richiesta<Iscritto>(`/admin/percorsi/${percorsoId}/iscritti`, { method: 'POST', json: { email } })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'iscritti', percorsoId] });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorso(percorsoId) });
    },
  });
}
export function useAnnullaIscrizione(percorsoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (iscrizioneId: string) => (await richiesta<unknown>(`/admin/iscrizioni/${iscrizioneId}`, { method: 'DELETE' })).status,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'iscritti', percorsoId] });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorso(percorsoId) });
    },
  });
}

// ---- import
export function useCaricaImport(percorsoId: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return (await richiesta<AnteprimaImport>(`/admin/percorsi/${percorsoId}/import`, { method: 'POST', body })).dati;
    },
  });
}
export function useConfermaImport(percorsoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string) => (await richiesta<EsitoImport>(`/admin/import/${importId}/conferma`, { method: 'POST' })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'iscritti', percorsoId] });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorso(percorsoId) });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.percorsi });
    },
  });
}
export function useAnnullaImport() {
  return useMutation({ mutationFn: async (importId: string) => (await richiesta<unknown>(`/admin/import/${importId}/annulla`, { method: 'POST' })).status });
}

// ---- webinar
export function useWebinarAdmin() {
  return useQuery({ queryKey: chiaviAdmin.webinarTutti, queryFn: async () => (await richiesta<Webinar[]>('/admin/webinar')).dati });
}
export function useWebinarAdminDettaglio(id: string) {
  return useQuery({ queryKey: chiaviAdmin.webinar(id), queryFn: async () => (await richiesta<Webinar>(`/admin/webinar/${id}`)).dati });
}
function invalidaWebinar(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: chiaviAdmin.webinarTutti });
  void qc.invalidateQueries({ queryKey: chiaviAdmin.percorsi });
  if (id) void qc.invalidateQueries({ queryKey: chiaviAdmin.webinar(id) });
}
export function useCreaWebinar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WebinarInput) => (await richiesta<Webinar>('/admin/webinar', { method: 'POST', json: input })).dati,
    onSuccess: () => invalidaWebinar(qc),
  });
}
export function useAggiornaWebinar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WebinarInput) => (await richiesta<Webinar>(`/admin/webinar/${id}`, { method: 'PUT', json: input })).dati,
    onSuccess: () => invalidaWebinar(qc, id),
  });
}
export function useAzioneWebinar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (azione: 'pubblica' | 'inviti/invia' | 'promemoria/invia') => (await richiesta<Webinar>(`/admin/webinar/${id}/${azione}`, { method: 'POST' })).dati,
    onSuccess: () => {
      invalidaWebinar(qc, id);
      void qc.invalidateQueries({ queryKey: ['admin', 'conferme', id] });
      void qc.invalidateQueries({ queryKey: chiaviAdmin.checklist(id) });
    },
  });
}
export function useConferme(webinarId: string, filtri: FiltriPaginati) {
  return useQuery({
    queryKey: chiaviAdmin.conferme(webinarId, filtri),
    queryFn: async () => (await richiesta<Paginato<ConfermaAdmin>>(`/admin/webinar/${webinarId}/conferme${query(filtri)}`)).dati,
    placeholderData: (prev) => prev,
  });
}
export function useChecklist(webinarId: string) {
  return useQuery({ queryKey: chiaviAdmin.checklist(webinarId), queryFn: async () => (await richiesta<VoceChecklist[]>(`/admin/webinar/${webinarId}/checklist`)).dati });
}
export function useAggiornaVoceChecklist(webinarId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { voceId: string; stato: 'da_fare' | 'fatta' | 'non_applicabile'; note?: string }) =>
      (await richiesta<VoceChecklist>(`/admin/checklist/${input.voceId}`, { method: 'PUT', json: { stato: input.stato, note: input.note ?? '' } })).dati,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaviAdmin.checklist(webinarId) }),
  });
}

// ---- utenti
export function useUtentiAdmin(filtri: FiltriPaginati) {
  return useQuery({
    queryKey: chiaviAdmin.utenti(filtri),
    queryFn: async () => (await richiesta<Paginato<UtenteAdmin>>(`/admin/utenti${query(filtri)}`)).dati,
    placeholderData: (prev) => prev,
  });
}
export function useCambiaRuolo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; ruolo: Ruolo }) => (await richiesta<UtenteAdmin>(`/admin/utenti/${input.id}/ruolo`, { method: 'PUT', json: { ruolo: input.ruolo } })).dati,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'utenti'] }),
  });
}
